export const REASON_BRAND = { navy: "#1C3F7E", orange: "#F47C24", teal: "#22CAD1" };

/** Compact square logo mark — shows the REASON icon (bars/arrow) cropped from the brand logo,
 * on a clean white tile so it reads on any sidebar/header background. */
export function ReasonLogoMark({ size = 34, className = "" }) {
  return (
    <span
      aria-label="REASON"
      className={`grid place-items-center rounded-lg bg-white border border-black/5 shadow-sm overflow-hidden shrink-0 ${className}`}
      style={{ width: size, height: size }}
    >
      <picture>
        <source srcSet="/brand/reason-logo.webp" type="image/webp" />
        <img
          src="/brand/reason-logo.jpg" alt="REASON"
          className="w-full object-cover object-top pointer-events-none"
          style={{ height: "240%", transform: "translateY(2%) scale(1.05)" }}
        />
      </picture>
    </span>
  );
}

export function ReasonLogo({ className = "", size = 34, showWordmark = true }) {
  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      <ReasonLogoMark size={size} />
      {showWordmark && (
        <span className="font-display font-bold tracking-tight text-foreground leading-none">
          REASON<span style={{ color: REASON_BRAND.orange }}>·</span>RetailOS
          <sup className="text-[0.55em] ml-0.5 text-muted-foreground">™</sup>
        </span>
      )}
    </div>
  );
}

/** Full vertical brand logo (icon + wordmark + tagline) for login/large surfaces. */
export function ReasonLogoFull({ className = "", width = 180 }) {
  return (
    <span className={`inline-grid place-items-center rounded-xl bg-white p-4 shadow-lg ${className}`}>
      <picture>
        <source srcSet="/brand/reason-logo.webp" type="image/webp" />
        <img src="/brand/reason-logo.jpg" alt="REASON — Analyzing for future" style={{ width }} className="object-contain" />
      </picture>
    </span>
  );
}

export function DpiitChip({ className = "" }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/60 px-2.5 py-1 text-[11px] font-medium text-muted-foreground ${className}`}
    >
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary" />
      DPIIT Registered • Made in India
      <span aria-hidden className="ml-0.5 inline-flex h-3 w-4 overflow-hidden rounded-[2px] border border-border/70">
        <span className="h-full w-full bg-gradient-to-b from-[#FF9933] via-white to-[#138808]" />
      </span>
    </span>
  );
}

export function FlagIN({ className = "" }) {
  return (
    <span aria-label="Made in India" className={`inline-flex h-3 w-4 overflow-hidden rounded-[2px] border border-border/70 ${className}`}>
      <span className="h-full w-full bg-gradient-to-b from-[#FF9933] via-white to-[#138808]" />
    </span>
  );
}

export const REASON_COMPANY = {
  legalName: "Reason Analytics Softech Pvt Ltd",
  website: "https://www.reason360.in",
  websiteShort: "www.reason360.in",
  contact: "sales@reasonanalytics.in",
  product: "REASON RetailOS™",
  country: "India",
  registered: "DPIIT Registered",
};

export function ReasonFooter({ className = "", compact = false }) {
  return (
    <div className={`flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-muted-foreground ${className}`}>
      <DpiitChip />
      <span className="text-foreground/80 font-medium">{REASON_COMPANY.legalName}</span>
      <a href={REASON_COMPANY.website} target="_blank" rel="noreferrer" className="hover:text-foreground">
        {REASON_COMPANY.websiteShort}
      </a>
      <a href={`mailto:${REASON_COMPANY.contact}`} className="hover:text-foreground">
        {REASON_COMPANY.contact}
      </a>
      <a href="/privacy-policy" className="hover:text-foreground" data-testid="footer-privacy-link">
        Privacy Policy
      </a>
      {!compact && (
        <span className="opacity-70">© {new Date().getFullYear()} {REASON_COMPANY.product}</span>
      )}
    </div>
  );
}
